import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpParams, HttpClient } from '@angular/common/http';
import { catchError, delay, Observable, switchMap, timeout, throwError } from 'rxjs';

import { v4 as uuidv4 } from 'uuid';
import * as CryptoJS from 'crypto-js';

import { environment } from '../../../environments/environment';
import StorageKeys from '../constants/storage';
import { StorageService } from '../services';

/**
 * HTTP Interceptor that adds a unique identifier to all outgoing requests
 * This helps with request tracking, debugging, and correlation across systems
 */
@Injectable()
export class IdentifierInterceptor implements HttpInterceptor {
  // Track ongoing email notifications to prevent duplicate calls
  private emailNotificationInProgress = new Set<string>();

  constructor(
    private http: HttpClient,
    private storageService: StorageService
  ) { }

  /**
   * Intercepts HTTP requests and adds identifier headers
   * @param req - The outgoing HTTP request
   * @param next - The next handler in the chain
   * @returns Observable of the HTTP event
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Bypass interceptor for authorize and api-error-log to prevent infinite loops
    if (req.url.includes('/oauth/authorize') || req.url.includes('/api-error-log')) {
      return next.handle(req);
    } else {
      const token = this.generateJWTToken('basic');
      return this._retryAuthoriserWith5xx(() => this.getAuthoriser(token.uuidVal), 2).pipe(
        timeout(30000),
        delay(600),
        switchMap((response: any) => {
          if (!!token?.accessToken) {
            let header: any = { 'Authorization': token.accessToken };
            let email: string = localStorage.getItem(StorageKeys.Email) || '';
            // Added api key in the header
            if (
              req.url.includes('?companyIdentifier') ||
              req.url.includes('?charityIdentifier') ||
              req.url.includes('/electricity/addresses') ||
              req.url.includes('/gas/addresses') ||
              req.url.includes('/electricity/meter') ||
              req.url.includes('/gas/meter') ||
              req.url.includes('/electricity/meter/usage') ||
              req.url.includes('/gas/meter/usage') ||
              req.url.includes('/electricity/quotes') ||
              req.url.includes('/gas/quotes') ||
              req.url.includes('/validate-bank-account') ||
              req.url.includes('customers/loa') ||
              (req.url.includes('customers/contract') && !req.url.includes('customers/contract/db'))
            ) {
              header['modifiedby'] = email || environment.PRODUCT || 'labrador';
            }
            let clone = req.clone({ setHeaders: header });
            const acquisitionId = environment.PRODUCT;
            const version = environment.IDENTIFIER;
            const moduleVal = environment.MODULE;

            if (req.method === 'POST' || req.method === 'PATCH') {
              // let bodyObj = req.body instanceof FormData ? req.body : { ...req.body };
              let bodyObj = req.body;
              let body;
              if (req.body instanceof FormData) {
                const param = new HttpParams({ fromString: 'version=' + encodeURIComponent(version) + '&module=' + moduleVal + '&subModule=' + acquisitionId.toUpperCase() });
                body = req.clone({ body: bodyObj, params: param, setHeaders: header });
              } else {
                bodyObj['module'] = moduleVal;
                bodyObj['subModule'] = acquisitionId.toUpperCase();
                bodyObj['version'] = version;
                body = req.clone({ body: bodyObj, setHeaders: header });
              }
              return this._retryRequestWith5xx(() => next.handle(body), 1).pipe(
                catchError((requestErr) => {
                  // Trigger email notification for 5xx errors on main API requests after retries
                  if (requestErr) {
                    this.triggerFailureEmailNotification(requestErr, body);
                  }
                  return throwError(() => requestErr);
                })
              );
            } else if (req.method === 'GET' || req.method === 'DELETE') {
              let body = req.clone({
                params: new HttpParams({ fromString: 'version=' + encodeURIComponent(version) + '&module=' + moduleVal + '&subModule=' + acquisitionId.toUpperCase() }), setHeaders: header
              });
              return this._retryRequestWith5xx(() => next.handle(body), 1).pipe(
                catchError((requestErr) => {
                  // Trigger email notification for 5xx errors on main API requests after retries
                  if (requestErr) {
                    this.triggerFailureEmailNotification(requestErr, body);
                  }
                  return throwError(() => requestErr);
                })
              );
            } else {
              return this._retryRequestWith5xx(() => next.handle(clone), 1).pipe(
                catchError((requestErr) => {
                  // Trigger email notification for 5xx errors on main API requests after retries
                  if (requestErr) {
                    this.triggerFailureEmailNotification(requestErr, req);
                  }
                  return throwError(() => requestErr);
                })
              );
            }
          } else {
            return this._retryRequestWith5xx(() => next.handle(req), 1).pipe(
              catchError((requestErr) => {
                // Trigger email notification for 5xx errors after retries even when no auth token
                if (requestErr) {
                  this.triggerFailureEmailNotification(requestErr, req);
                }
                return throwError(() => requestErr);
              })
            );
          }
        }),
        catchError((err) => {
          // If authoriser fails completely, let the original error propagate to components
          // This allows component-level error handling while still providing interceptor logging
          if (err.status === 404) {
            return throwError(() => err); // Preserve original error for component handling
          }

          // Trigger email notification for 5xx errors after all retries exhausted
          if (err.status >= 500 && err.status < 600) {
            this.triggerFailureEmailNotification(err, { method: 'POST', url: 'oauth/authorize' });
          }

          // For other errors, preserve the original error structure
          return throwError(() => err);
        })
      );
    }
  }

  // Custom retry logic for 5xx errors only on authoriser calls
  private _retryAuthoriserWith5xx<T>(requestFn: () => Observable<T>, retries: number): Observable<T> {
    return new Observable<T>((subscriber) => {
      const attempt = (remaining: number) => {
        requestFn().subscribe({
          next: (value) => {
            subscriber.next(value);
            subscriber.complete(); // Complete the observable on success
          },
          error: (err) => {
            // Only retry for 5xx errors, not 4xx (like 404)
            if (remaining > 0) {
              setTimeout(() => attempt(remaining - 1), 1000); // 1 second delay
            } else {
              // Don't retry for 4xx errors (client errors like 404, 401, etc.)
              // For 5xx errors that exhausted retries, trigger email notification
              this.triggerFailureEmailNotification(err, { method: 'POST', url: 'oauth/authorize' });
              subscriber.error(err);
            }
          }
        });
      };
      attempt(retries);
    });
  }

  // Custom retry logic for 5xx errors on main API requests
  private _retryRequestWith5xx<T>(requestFn: () => Observable<T>, retries: number): Observable<T> {
    return new Observable<T>((subscriber) => {
      const attempt = (remaining: number) => {
        requestFn().subscribe({
          next: (value) => {
            subscriber.next(value);
          },
          complete: () => {
            subscriber.complete();
          },
          error: (err) => {
            // Only retry 1 time
            if (remaining > 0) {
              setTimeout(() => attempt(remaining - 1), 1000); // 1 second delay
            } else {
              // After retries exhausted, propagate the error
              subscriber.error(err);
            }
          }
        });
      };
      attempt(retries);
    });
  }

  // Get authoriser
  getAuthoriser(key: any): Observable<any> {
    const URL = environment.AUTH_URL + 'oauth/authorize';
    return this.http.post(URL, { idToken: key });
  }

  // Generate a JWT token
  private generateJWTToken(keyId: string): any {
    let uuid = uuidv4();
    const header = {
      alg: "HS256",
      typ: "JWT"
    };
    const data = {
      token_use: 'id',
      jti: String(uuid),
      keyid: keyId
    };

    const token = this.getToken(header, data);
    uuid = uuid.concat('function()');

    const cryptoEnc = CryptoJS.SHA256(uuid);
    uuid = CryptoJS.enc.Hex.stringify(cryptoEnc);

    return {
      uuidVal: uuid,
      accessToken: token
    };
  }

  // Get JWT token
  private getToken(header: any, data: any): any {
    const stringifiedHeader = CryptoJS.enc.Utf8.parse(JSON.stringify(header));
    const encodedHeader = this.base64url(stringifiedHeader);

    const stringifiedData = CryptoJS.enc.Utf8.parse(JSON.stringify(data));
    const encodedData = this.base64url(stringifiedData);

    const token = encodedHeader + "." + encodedData;

    const signature = CryptoJS.HmacSHA256(token, 'const');
    const encodedsignature = this.base64url(signature);

    const signedToken = token + "." + encodedsignature;

    return signedToken;
  }

  /**
   * @description Converts the given source to a base64url encoded string
   * @param source The source data to encode
   * @returns The base64url encoded string
   */
  private base64url(source: any): string {
    let encodedSource = CryptoJS.enc.Base64.stringify(source);

    encodedSource = encodedSource.replace(/=+$/, '');

    encodedSource = encodedSource.replace(/\+/g, '-');
    encodedSource = encodedSource.replace(/\//g, '_');

    return encodedSource;
  }

  /**
   * Triggers email notification API when critical API failures occur
   * @param error - The error object
   * @param req - The request object that failed
   */
  private triggerFailureEmailNotification(error: any, req: any): void {
    try {
      // Create unique key for this error to prevent duplicate notifications
      const errorKey = `${req.method}-${req.url}-${error.status}`;
      
      // If notification already in progress for this error, skip
      if (this.emailNotificationInProgress.has(errorKey)) {
        console.warn('Email notification already in progress for:', errorKey);
        return;
      }
      
      // Mark notification as in progress
      this.emailNotificationInProgress.add(errorKey);

      const apiName = req.url.split(environment.API_URL)?.[1] || 'Unknown API';
      
      const notificationPayload = {
        source: 'UI',
        customerId: this.storageService.getItemFromSessionStorage(StorageKeys.CUSTOMER_ID) || 'UNKNOWN',
        requestMethod: req.method || 'UNKNOWN',
        requestUrl: req.urlWithParams || req.url || 'UNKNOWN',
        statusCode: error.status || error.code || 'UNKNOWN',
        errorMessage: error?.message || error?.statusText || error?.error?.message || 'Unknown error',
        requestBody: req.body ? {
          ...req.body,
          acqId: environment.PRODUCT
        } : { acqId: environment.PRODUCT },
        responseBody: error || 'No response body',
        clientInfo: {
          userAgent: navigator.userAgent,
          browser: navigator.appName || 'UNKNOWN',
          platform: navigator.platform || 'UNKNOWN',
          language: navigator.language || 'UNKNOWN',
          timestamp: new Date().toISOString(),
          pageUrl: window.location.href,
          screen: window.screen.width + 'x' + window.screen.height
        },
        subject: `Failure Report [${environment.ENV} - BUSINESS - ${environment.PRODUCT}] : ${req.method} ${apiName} API Fails`,
        apiName: apiName
      };

      // Send email notification with single retry, then remove from tracking
      this.sendEmailWithRetry(notificationPayload, errorKey, 1);
    } catch (notificationError) {
      console.error('Error in email notification system:', notificationError);
    }
  }

  /**
   * Send email notification with retry logic (1 retry only)
   * @param payload - The notification payload
   * @param errorKey - Unique key for this error
   * @param remainingRetries - Number of retries remaining
   */
  private sendEmailWithRetry(payload: any, errorKey: string, remainingRetries: number): void {
    this.http.post(environment.AUTH_URL + 'bus/v1/api-error-log', payload).subscribe({
      next: () => {
        // Remove from tracking on success
        this.emailNotificationInProgress.delete(errorKey);
      },
      error: (emailErr) => {
        console.error('Failed to send email notification:', emailErr);
        
        if (remainingRetries > 0) {
          setTimeout(() => {
            this.sendEmailWithRetry(payload, errorKey, remainingRetries - 1);
          }, 2000); // 2 second delay before retry
        } else {
          console.error('All email notification retries exhausted');
          // Remove from tracking after all retries exhausted
          this.emailNotificationInProgress.delete(errorKey);
        }
      }
    });
  }
}
